import type { EnhancedSmartLink } from './smartLinkTypes.ts';

type LinkIdentity = Pick<EnhancedSmartLink, 'ari' | 'url'>;

/**
 * The url without its query string or hash, so `…/page?t=1` and `…/page?t=2#top` compare equal.
 * A url that does not parse is returned unchanged and only matches itself.
 */
export const stripUrlQueryAndHash = (url: string): string => {
	try {
		const { origin, pathname } = new URL(url);
		return `${origin}${pathname}`;
	} catch {
		return url;
	}
};

/**
 * Returns a predicate that reports whether a link duplicates one it has already kept, and keeps it
 * when it does not. Call it on links in display order: the first link of a set is the one kept.
 *
 * Two links are duplicates when both resolved to the same `ari`, or when either has no `ari` and
 * their urls match once the query string and hash are dropped. Two different `ari`s are never
 * duplicates, even on the same path: `viewpage.action?pageId=1` and `?pageId=2` are two pages.
 *
 * `seed` links are kept up front, so a later page of links can be checked against earlier ones.
 */
export const createLinkDeduper = (
	seed: readonly LinkIdentity[] = [],
): ((link: LinkIdentity) => boolean) => {
	const keptAris = new Set<string>();
	const keptPaths = new Set<string>();
	// Paths of kept links without an ari: the only kept links a resolved link can match by path.
	const keptPathsWithoutAri = new Set<string>();

	const isDuplicate = (link: LinkIdentity): boolean => {
		const path = stripUrlQueryAndHash(link.url);
		const duplicate = link.ari
			? keptAris.has(link.ari) || keptPathsWithoutAri.has(path)
			: keptPaths.has(path);
		if (duplicate) {
			return true;
		}

		keptPaths.add(path);
		if (link.ari) {
			keptAris.add(link.ari);
		} else {
			keptPathsWithoutAri.add(path);
		}
		return false;
	};

	seed.forEach(isDuplicate);
	return isDuplicate;
};
